import React, { useEffect, useState } from "react";
import { Gift, Copy, Check, Share2, Users, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function ReferralCard() {
  const [d, setD] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/growth/referral").then((r) => setD(r.data)).catch(() => setD(false));
  }, []);

  if (!d) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(d.link);
      setCopied(true);
      toast.success("Invite link copied! 🔗");
      setTimeout(() => setCopied(false), 1800);
    } catch { toast(d.link); }
  };

  const share = async () => {
    const text = `Build any game you imagine with GameView Studio — join with my link and we both get bonus AI credits! ${d.link}`;
    try {
      if (navigator.share) await navigator.share({ title: "GameView Studio", text, url: d.link });
      else { await navigator.clipboard.writeText(d.link); toast.success("Invite link copied! 🔗"); }
    } catch { /* user dismissed */ }
  };

  return (
    <div data-testid="referral-card"
      className="relative overflow-hidden rounded-2xl border border-fuchsia-500/25 bg-gradient-to-br from-fuchsia-500/[0.10] via-[#3b9eff]/[0.05] to-transparent p-5 mb-6">
      <div className="absolute -right-6 -top-6 w-28 h-28 rounded-full bg-fuchsia-500/10 blur-2xl" />
      <div className="relative flex items-start gap-3">
        <div className="w-11 h-11 shrink-0 rounded-xl grid place-items-center bg-gradient-to-br from-fuchsia-500 to-[#3b9eff]">
          <Gift className="w-5 h-5 text-white" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-display text-lg font-extrabold">Invite creators, earn credits</h3>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-0.5">
            Give a friend <b className="text-[hsl(var(--foreground))]">{d.referee_reward}</b> bonus AI credits — get{" "}
            <b className="text-fuchsia-400">{d.referrer_reward}</b> yourself when they build their first game.
          </p>

          <div className="mt-3 flex items-center gap-2 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--background))]/60 p-2 pl-3">
            <span className="text-xs text-[hsl(var(--muted-foreground))] shrink-0">Your link</span>
            <span data-testid="referral-link" className="flex-1 min-w-0 truncate text-sm font-mono">{d.link}</span>
            <Button size="sm" variant="outline" className="rounded-full h-8 shrink-0" onClick={copy} data-testid="referral-copy">
              {copied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
            </Button>
            <Button size="sm" className="rounded-full h-8 shrink-0 bg-gradient-to-r from-fuchsia-500 to-[#3b9eff]" onClick={share} data-testid="referral-share">
              <Share2 className="w-4 h-4" /> Share
            </Button>
          </div>

          <div className="mt-3 flex items-center gap-5 text-sm">
            <span className="flex items-center gap-1.5" data-testid="referral-invited">
              <Users className="w-4 h-4 text-[#3b9eff]" /> <b>{d.invited}</b>
              <span className="text-[hsl(var(--muted-foreground))]">invited</span>
            </span>
            <span className="flex items-center gap-1.5" data-testid="referral-activated">
              <Sparkles className="w-4 h-4 text-amber-400" /> <b>{d.activated}</b>
              <span className="text-[hsl(var(--muted-foreground))]">building</span>
            </span>
            <span className="flex items-center gap-1.5" data-testid="referral-earned">
              <Gift className="w-4 h-4 text-fuchsia-400" /> <b>{d.credits_earned}</b>
              <span className="text-[hsl(var(--muted-foreground))]">credits earned</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
